import { useCallback, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Badge,
  Button,
  Form,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Table,
} from 'reactstrap';
import {
  TournamentCurrencyLedgerEntryDto,
  TournamentCurrencyLedgerType,
  TournamentDollarLedgerType,
} from '@repo/common-types';
import {
  useAddTournamentDollarsMutation,
  useCreateTicketForUserMutation,
  useGetEligibleTargetTournamentsQuery,
  useGetTournamentCurrencyLedgerQuery,
  useGetUserTicketsQuery,
} from '@/redux/api/tournaments';
import { format, formatDistanceToNow } from 'date-fns';
import { toast } from 'react-toastify';

import Paginator from '@/pages/common/paginator';
import { ReactComponent as TournamentTicketIcon } from '@/assets/tokens/tournament-ticket.svg';
import { ReactComponent as TournamentDollarsIcon } from '@/assets/tokens/tournament-dollars.svg';

const getTypeConfig = (type: TournamentCurrencyLedgerType) => {
  const isTicket =
    type === TournamentCurrencyLedgerType.TICKET_RECEIVED || type === TournamentCurrencyLedgerType.TICKET_USED;
  const isCredit =
    type === TournamentCurrencyLedgerType.TICKET_RECEIVED || type === TournamentCurrencyLedgerType.TD_CREDIT;

  return {
    label: isCredit ? 'CREDIT' : 'DEBIT',
    badgeColor: isCredit ? 'success' : 'danger',
    isCredit,
    isTicket,
  };
};

const TournamentCurrencyLedgerSection = () => {
  const { userId } = useParams();
  const [skip, setSkip] = useState(0);
  const [take, setTake] = useState(10);
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState<'ticket' | 'dollars'>('ticket');
  const [tournamentId, setTournamentId] = useState('');
  const [dollarAmount, setDollarAmount] = useState(0);
  const [dollarOperation, setDollarOperation] = useState<TournamentDollarLedgerType>(TournamentDollarLedgerType.CREDIT);

  const { data, isLoading } = useGetTournamentCurrencyLedgerQuery(
    { userId: userId as string, skip, take },
    { skip: !userId },
  );

  const { data: tournamentsData } = useGetEligibleTargetTournamentsQuery();
  const { data: userTicketsData } = useGetUserTicketsQuery(
    { userId: userId as string, skip: 0, take: 10000 },
    { skip: !userId },
  );
  const [addTournamentDollars] = useAddTournamentDollarsMutation();
  const [createTicketForUser] = useCreateTicketForUserMutation();

  const entries = data?.data || [];
  const tdBalance = data?.tdBalance ?? 0;

  const userTicketTournamentIds = useMemo(() => {
    const tickets = userTicketsData?.tickets || [];
    return tickets.reduce((acc, ticket) => {
      if (ticket.tournamentId) {
        acc.add(ticket.tournamentId);
      }
      return acc;
    }, new Set<string>());
  }, [userTicketsData?.tickets]);

  const eligibleTournaments = tournamentsData?.data?.filter(
    (tournament) => !userTicketTournamentIds.has(tournament.id),
  );

  const handlePageChange = useCallback((newSkip: number, newTake: number) => {
    setSkip(newSkip);
    setTake(newTake);
  }, []);

  const handleOpenModal = (type: 'ticket' | 'dollars') => {
    setModalType(type);
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setTournamentId('');
    setDollarAmount(0);
    setDollarOperation(TournamentDollarLedgerType.CREDIT);
  };

  const handleSubmit = async () => {
    if (modalType === 'ticket') {
      if (!tournamentId) {
        toast.error('Please select a tournament');
        return;
      }
      if (!userId) {
        toast.error('User ID is missing');
        return;
      }

      try {
        await createTicketForUser({
          userId,
          tournamentId,
        }).unwrap();
        toast.success('Ticket creation initiated successfully');
        handleCloseModal();
      } catch (error: any) {
        const errorMessage = error?.data?.message || 'Failed to create ticket';
        toast.error(errorMessage);
      }
    } else {
      if (!dollarAmount || dollarAmount <= 0) {
        toast.error('Please enter a valid amount');
        return;
      }
      if (!userId) {
        toast.error('User ID is missing');
        return;
      }

      const actionText = dollarOperation === TournamentDollarLedgerType.CREDIT ? 'credit' : 'debit';
      try {
        await addTournamentDollars({
          userId,
          amount: dollarAmount,
          type: dollarOperation,
        }).unwrap();
        toast.success(`Tournament dollars ${actionText} queued successfully`);
        handleCloseModal();
      } catch {
        toast.error(`Failed to ${actionText} tournament dollars`);
      }
    }
  };

  const renderAmount = (entry: TournamentCurrencyLedgerEntryDto) => {
    const typeConfig = getTypeConfig(entry.type);
    if (entry.amount === null) return '-';

    const sign = typeConfig.isCredit ? '+' : '-';
    const colorClass = typeConfig.isCredit ? 'text-success' : 'text-danger';
    const iconStyle = { width: '16px', height: '16px' };

    const displayAmount = typeConfig.isTicket ? '1' : `$${entry.amount.toFixed(2)}`;

    return (
      <span className={`fw-semibold ${colorClass} d-flex align-items-center gap-1`}>
        {typeConfig.isTicket ? <TournamentTicketIcon style={iconStyle} /> : <TournamentDollarsIcon style={iconStyle} />}
        {`${sign}${displayAmount}`}
      </span>
    );
  };

  return (
    <div className="my-4">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <div>
          <h3>Tournament Currency Ledger</h3>
          <div className="d-flex align-items-center mt-2">
            <span className="text-muted me-2">T$ Balance:</span>
            <h4 className="text-primary mb-0">${tdBalance.toFixed(2)}</h4>
          </div>
        </div>
        <div className="d-flex gap-2">
          <Button color="success" size="sm" onClick={() => handleOpenModal('ticket')}>
            <i className="ri-ticket-line me-1"></i>
            Add Ticket
          </Button>
          <Button color="primary" size="sm" onClick={() => handleOpenModal('dollars')}>
            <i className="ri-money-dollar-circle-line me-1"></i>
            Adjust T$
          </Button>
        </div>
      </div>

      <div className="table-responsive">
        <Table className="table-nowrap table-hover mb-0" style={{ borderTop: '2px solid #000' }}>
          <thead className="table-light">
            <tr>
              <th>Date</th>
              <th>Type</th>
              <th>Tournament</th>
              <th>T$ Balance</th>
              <th>Type</th>
              <th>Amount</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={6} className="py-4 text-center">
                  <div className="spinner-border spinner-border-sm text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
            ) : entries.length === 0 ? (
              <tr>
                <td colSpan={6} className="py-4 text-center">
                  <i className="ri-file-list-3-line ri-2x text-muted d-block mb-2"></i>
                  <span className="text-muted">No transactions found</span>
                </td>
              </tr>
            ) : (
              entries.map((entry, index) => {
                const typeConfig = getTypeConfig(entry.type);
                return (
                  <tr key={`${entry.type}-${entry.date}-${index}`}>
                    <td>
                      <small>{formatDistanceToNow(new Date(entry.date), { addSuffix: true })}</small>
                    </td>
                    <td>
                      <Badge color={typeConfig.badgeColor} style={{ width: 'fit-content' }}>
                        {typeConfig.label}
                      </Badge>
                    </td>
                    <td>
                      {entry.tournamentName ? (
                        <Link
                          to={`/tournaments/details/${entry.tournamentId}`}
                          className="text-primary text-decoration-none"
                        >
                          {entry.tournamentName}
                          <i className="ri-external-link-line small ms-1"></i>
                        </Link>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      {entry.tdBalanceAfter !== null ? (
                        <span className="text-muted">${entry.tdBalanceAfter.toFixed(2)}</span>
                      ) : (
                        '-'
                      )}
                    </td>
                    <td>
                      <small className="text-muted">{entry.referenceType || '-'}</small>
                    </td>
                    <td>{renderAmount(entry)}</td>
                  </tr>
                );
              })
            )}
          </tbody>
        </Table>
      </div>

      <Paginator
        take={take}
        skip={skip}
        setTake={(newTake) => handlePageChange(skip, newTake)}
        setSkip={(newSkip) => handlePageChange(newSkip, take)}
        totalCount={data?.total || 0}
        showing={entries.length}
      />

      <Modal isOpen={showModal} toggle={handleCloseModal}>
        <ModalHeader toggle={handleCloseModal}>
          {modalType === 'ticket' ? 'Add Tournament Ticket' : 'Adjust Tournament Dollars'}
        </ModalHeader>
        <ModalBody>
          <Form>
            {modalType === 'ticket' ? (
              <FormGroup>
                <Label for="tournament">Select Tournament</Label>
                {eligibleTournaments && eligibleTournaments.length === 0 ? (
                  <div className="alert alert-info mb-0">
                    <i className="ri-information-line me-1"></i>
                    No available tournaments. User already has tickets for all upcoming tournaments.
                  </div>
                ) : (
                  <>
                    <Input
                      type="select"
                      id="tournament"
                      value={tournamentId}
                      onChange={(e) => setTournamentId(e.target.value)}
                    >
                      <option value="">-- Select Tournament --</option>
                      {eligibleTournaments?.map((tournament) => (
                        <option key={tournament.id} value={tournament.id}>
                          {tournament.name} - ${tournament.totalBuyin} -{' '}
                          {tournament.startTime ? format(tournament.startTime, 'MM/dd/yyyy HH:mm') : ''}
                        </option>
                      ))}
                    </Input>
                    <small className="text-muted">Only one ticket per user per tournament is allowed</small>
                  </>
                )}
              </FormGroup>
            ) : (
              <>
                <FormGroup>
                  <Label for="operation">Operation</Label>
                  <Input
                    type="select"
                    id="operation"
                    value={dollarOperation}
                    onChange={(e) => setDollarOperation(e.target.value as TournamentDollarLedgerType)}
                  >
                    <option value={TournamentDollarLedgerType.CREDIT}>Credit (Add)</option>
                    <option value={TournamentDollarLedgerType.DEBIT}>Debit (Remove)</option>
                  </Input>
                </FormGroup>
                <FormGroup>
                  <Label for="amount">Amount ($)</Label>
                  <Input
                    type="number"
                    id="amount"
                    min="0"
                    step="0.01"
                    value={dollarAmount || ''}
                    onChange={(e) => {
                      const value = e.target.value;
                      const parsed = parseFloat(value);
                      setDollarAmount(value === '' || Number.isNaN(parsed) ? 0 : parsed);
                    }}
                    placeholder="Enter amount"
                  />
                </FormGroup>
              </>
            )}
          </Form>
        </ModalBody>
        <ModalFooter>
          <Button color="secondary" onClick={handleCloseModal}>
            Cancel
          </Button>
          <Button
            color="primary"
            onClick={handleSubmit}
            disabled={modalType === 'ticket' && (!eligibleTournaments || eligibleTournaments.length === 0)}
          >
            {modalType === 'ticket'
              ? 'Create Ticket'
              : dollarOperation === TournamentDollarLedgerType.CREDIT
                ? 'Add Dollars'
                : 'Remove Dollars'}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default TournamentCurrencyLedgerSection;
